import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ClipboardList, Archive, Trash2, CheckCircle, History, Clock, RotateCcw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1`;

export default function ReturnsInspectionPage() {
    const [pendingReturns, setPendingReturns] = useState([]);
    const [processedReturns, setProcessedReturns] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Estado para el modal de inspección
    const [selectedReturn, setSelectedReturn] = useState(null);
    const [inspectionItems, setInspectionItems] = useState({}); // { productId: 'restock' | 'discard' }
    const [isProcessing, setIsProcessing] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    // Estado para el modal de historial
    const [historyReturn, setHistoryReturn] = useState(null);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);

    useEffect(() => {
        fetchReturns();
    }, []);

    const fetchReturns = async () => {
        setIsLoading(true);
        try {
            const [pendingRes, processedRes] = await Promise.all([
                axios.get(`${API}/returns/?status=pending`),
                axios.get(`${API}/returns/?status=processed`),
            ]);
            setPendingReturns(pendingRes.data || []);
            setProcessedReturns(processedRes.data || []);
        } catch (error) {
            console.error('Error fetching returns:', error);
            toast.error("No se pudieron cargar las devoluciones.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleInspect = (returnDoc) => {
        setSelectedReturn(returnDoc);
        // Inicializar decisiones por defecto a 'restock' (o dejarlas vacías para obligar a elegir)
        const initialDecisions = {};
        returnDoc.items.forEach(item => {
            initialDecisions[item.product_id] = 'restock';
        });
        setInspectionItems(initialDecisions);
        setIsDialogOpen(true);
    };

    const handleDecisionChange = (productId, decision) => {
        setInspectionItems(prev => ({
            ...prev,
            [productId]: decision
        }));
    };

    const handleSubmitInspection = async () => {
        setIsProcessing(true);
        try {
            const itemsPayload = Object.entries(inspectionItems).map(([productId, action]) => ({
                product_id: productId,
                action: action
            }));

            await axios.post(`${API}/returns/${selectedReturn.id}/process`, {
                items: itemsPayload
            });

            toast.success("Devolución procesada y stock actualizado correctamente.");
            setIsDialogOpen(false);
            fetchReturns(); // Recargar lista
        } catch (error) {
            console.error(error);
            toast.error("Error al procesar la inspección.");
        } finally {
            setIsProcessing(false);
        }
    };

    if (isLoading) return <div className="p-8 text-center">Cargando devoluciones...</div>;

    const allHistory = processedReturns
        .flatMap(ret => (ret.history || []).map(entry => ({ ...entry, returnId: ret.id, saleId: ret.sale_id, reason: ret.reason })))
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold flex items-center gap-2">
                <ClipboardList className="size-8" /> Inspección de Devoluciones
            </h1>

            <Tabs defaultValue="pending" className="w-full">
                <TabsList>
                    <TabsTrigger value="pending" className="flex items-center gap-2">
                        <ClipboardList className="size-4" /> Pendientes
                    </TabsTrigger>
                    <TabsTrigger value="history" className="flex items-center gap-2">
                        <History className="size-4" /> Historial Global
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="pending">
                    <Card>
                        <CardHeader>
                            <CardTitle>Pendientes de Revisión</CardTitle>
                            <CardDescription>
                                Revise los productos devueltos y decida si reingresan al stock o se desechan.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {pendingReturns.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">No hay devoluciones pendientes de inspección.</p>
                            ) : (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Fecha</TableHead>
                                            <TableHead>ID Venta Original</TableHead>
                                            <TableHead>Motivo</TableHead>
                                            <TableHead className="text-right">Items</TableHead>
                                            <TableHead className="text-right">Acción</TableHead>
                                            <TableHead className="text-right">Historial</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {pendingReturns.map((ret) => (
                                            <TableRow key={ret.id}>
                                                <TableCell>{new Date(ret.created_at).toLocaleDateString()}</TableCell>
                                                <TableCell className="font-mono text-xs">{ret.sale_id.substring(0, 8)}...</TableCell>
                                                <TableCell className="max-w-md truncate" title={ret.reason}>{ret.reason}</TableCell>
                                                <TableCell className="text-right">{ret.items.length}</TableCell>
                                                <TableCell className="text-right">
                                                    <Button size="sm" onClick={() => handleInspect(ret)}>
                                                        <CheckCircle className="mr-2 size-4" /> Inspeccionar
                                                    </Button>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button size="sm" variant="outline" onClick={() => { setHistoryReturn(ret); setIsHistoryOpen(true); }}>
                                                        <History className="mr-2 size-4" /> Ver Historial
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="history">
                    <Card>
                        <CardHeader>
                            <CardTitle>Historial Global de Devoluciones</CardTitle>
                            <CardDescription>
                                Todos los eventos registrados en el ciclo de vida de las devoluciones procesadas.
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {allHistory.length === 0 ? (
                                <p className="text-center text-muted-foreground py-8">No hay devoluciones procesadas aún.</p>
                            ) : (
                                <div className="space-y-4">
                                    {allHistory.map((entry, idx) => (
                                        <div key={idx} className="flex gap-3 border-l-2 border-primary pl-4 relative pb-3">
                                            <div className="flex flex-col gap-1 w-full">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-medium capitalize flex items-center gap-1">
                                                        {entry.event === 'created' ? <Clock className="size-3.5" /> : <RotateCcw className="size-3.5" />}
                                                        {entry.event === 'created' ? 'Creada' : 'Procesada'}
                                                    </span>
                                                    <span className="text-xs text-muted-foreground">{new Date(entry.timestamp).toLocaleString()}</span>
                                                </div>
                                                <p className="text-sm text-muted-foreground">{entry.description}</p>
                                                <div className="flex gap-2 text-xs text-muted-foreground">
                                                    <span>Por: {entry.user_name}</span>
                                                    <span>•</span>
                                                    <span>Venta: {entry.saleId.substring(0, 8)}...</span>
                                                    <span>•</span>
                                                    <span className="max-w-xs truncate" title={entry.reason}>Motivo: {entry.reason}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Modal de Inspección */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="max-w-4xl">
                    <DialogHeader>
                        <DialogTitle>Procesar Devolución</DialogTitle>
                        <DialogDescription>
                            Para cada producto, determine su destino final.
                        </DialogDescription>
                    </DialogHeader>

                    {selectedReturn && (
                        <div className="space-y-4">
                            <div className="bg-muted p-3 rounded-md text-sm">
                                <p><strong>Motivo del Cliente:</strong> {selectedReturn.reason}</p>
                            </div>

                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Producto</TableHead>
                                        <TableHead className="text-right">Cant.</TableHead>
                                        <TableHead>Decisión</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {selectedReturn.items.map((item) => (
                                        <TableRow key={item.product_id}>
                                            <TableCell>{item.product_name}</TableCell>
                                            <TableCell className="text-right font-bold">{item.quantity}</TableCell>
                                            <TableCell>
                                                <RadioGroup
                                                    value={inspectionItems[item.product_id]}
                                                    onValueChange={(val) => handleDecisionChange(item.product_id, val)}
                                                    className="flex items-center gap-4"
                                                >
                                                    <div className="flex items-center space-x-2 border p-2 rounded-md hover:bg-green-50 data-[state=checked]:border-green-500 cursor-pointer">
                                                        <RadioGroupItem value="restock" id={`r-${item.product_id}`} />
                                                        <Label htmlFor={`r-${item.product_id}`} className="flex items-center cursor-pointer">
                                                            <Archive className="size-4 mr-1 text-green-600" /> Reingresar
                                                        </Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2 border p-2 rounded-md hover:bg-red-50 cursor-pointer">
                                                        <RadioGroupItem value="discard" id={`d-${item.product_id}`} />
                                                        <Label htmlFor={`d-${item.product_id}`} className="flex items-center cursor-pointer">
                                                            <Trash2 className="size-4 mr-1 text-red-600" /> Desechar
                                                        </Label>
                                                    </div>
                                                </RadioGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>

                            <DialogFooter>
                                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
                                <Button onClick={handleSubmitInspection} disabled={isProcessing}>
                                    {isProcessing ? "Procesando..." : "Confirmar y Finalizar"}
                                </Button>
                            </DialogFooter>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Modal de Historial */}
            <Dialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <History className="size-5" /> Historial de Devolución
                        </DialogTitle>
                        <DialogDescription>
                            Eventos registrados en el ciclo de vida de esta devolución.
                        </DialogDescription>
                    </DialogHeader>

                    {historyReturn && (
                        <div className="space-y-4">
                            <div className="bg-muted p-3 rounded-md text-sm">
                                <p><strong>ID Venta:</strong> {historyReturn.sale_id}</p>
                                <p><strong>Motivo:</strong> {historyReturn.reason}</p>
                                <p><strong>Estado:</strong> <Badge variant={historyReturn.status === 'processed' ? 'default' : 'secondary'}>{historyReturn.status}</Badge></p>
                            </div>

                            {historyReturn.history && historyReturn.history.length > 0 ? (
                                <div className="space-y-3">
                                    {historyReturn.history.map((entry, idx) => (
                                        <div key={idx} className="flex gap-3 border-l-2 border-primary pl-4 relative pb-3">
                                            <div className="flex flex-col gap-1 w-full">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-medium capitalize">{entry.event === 'created' ? 'Creada' : 'Procesada'}</span>
                                                    <span className="text-xs text-muted-foreground">{new Date(entry.timestamp).toLocaleString()}</span>
                                                </div>
                                                <p className="text-sm text-muted-foreground">{entry.description}</p>
                                                <p className="text-xs text-muted-foreground">Por: {entry.user_name}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-center text-muted-foreground py-4">No hay historial registrado.</p>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsHistoryOpen(false)}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
